// Original hypothetical lessons; no product recommendations.
export const researchLessons = [
  {
    "id": "index-weighting",
    "moduleId": "research",
    "title": "同樣三家公司，指數為什麼漲得不一樣？",
    "subtitle": "市值加權與等權重的差別",
    "minutes": 6,
    "tag": "基金研究",
    "objectives": [
      "分辨持股名單和配置權重",
      "用期初權重計算一期組合報酬"
    ],
    "paragraphs": [
      "指數不只決定選哪些公司，也決定每家公司占多少。市值加權通常依特定市值口徑給比重；實際規則可能使用自由流通市值、權重上限及其他調整，應讀該指數的編製方法，不能只看名稱。",
      "等權重是在指定調整時點給各成分相同比重。之後價格漲跌不同，權重就會漂移；它不是每天自動保持平均，也不代表產業分散或風險相同。兩個指數即使名單完全一致，表現也可能不同。",
      "下面只看一個期間，使用期初權重，沒有加減碼、現金流、配息或費用。各成分報酬乘上期初比重後加總，是這個簡化組合的報酬；不能把它當成哪種方法長期更好的證據。"
    ],
    "example": {
      "title": "假設資料，練習自己核對",
      "steps": [
        "三家虛構公司甲、乙、丙的期初市值比為 60：30：10；本期報酬分別為 10%、0%、−10%。",
        "市值加權：60%×10%＋30%×0%＋10%×(−10%)＝5%。",
        "期初各三分之一的等權重組合：10%÷3＋0%÷3−10%÷3＝0%。",
        "同一份名單出現 5 個百分點差異，來自配置權重。沒有交易時，等權重組合的甲占比也會隨漲幅增加。"
      ],
      "conclusion": "相同名稱或名單不足以判定相同曝險，要再看權重規則。"
    },
    "pitfalls": [
      "以為等權重永遠維持不變。",
      "把本例結果當成市值加權必定勝出的證明。"
    ],
    "glossary": [
      {
        "term": "期初權重",
        "definition": "一個觀察期間開始時，各項資產占組合價值的比例。"
      },
      {
        "term": "權重漂移",
        "definition": "在沒有調整持有數量時，價格變動讓各資產占比改變。"
      }
    ],
    "takeaway": "相同名稱或名單不足以判定相同曝險，要再看權重規則。",
    "exercise": "把三家的期初比重改成 20%、30%、50%，重算同一期間報酬，並說明比較時固定了哪些條件。",
    "quiz": {
      "question": "期初權重 50%、30%、20%，各自報酬 8%、−2%、5%；忽略其他因素，組合一期報酬是多少？",
      "options": [
        "3.67%，把三個報酬直接平均",
        "4.4%，用期初比重加權",
        "11%，直接加總三個報酬"
      ],
      "answer": 1,
      "explanation": "50%×8%＋30%×(−2%)＋20%×5%＝4.4%。"
    },
    "sources": [
      {
        "label": "市場先生｜ETF 購買前的比較項目",
        "url": "https://rich01.com/how-buy-etfs/",
        "kind": "reading"
      },
      {
        "label": "證交所｜市值與等權重的差異",
        "url": "https://www.twse.com.tw/zh/ETFortune/advisorDoc/ff80808189d463ea0189dd39c9de003a",
        "kind": "official"
      }
    ]
  },
  {
    "id": "fund-overlap",
    "moduleId": "research",
    "title": "買了兩檔 ETF，實際買到幾種曝險？",
    "subtitle": "把基金拆回持股再合併",
    "minutes": 6,
    "tag": "基金研究",
    "objectives": [
      "計算同一公司的合併權重",
      "區分檔數重疊、權重重疊與報酬相關性"
    ],
    "paragraphs": [
      "兩檔基金名稱不同，也可能同時持有同一家公司。檢查時先對齊持股日期、證券識別與權重分母；名稱近似不保證同一證券。同公司不同股別是否合併，也要先定義。",
      "本課把每檔基金的權重乘以它在總組合的比重，再按相同證券加總。另用「每個共同成分在兩基金權重的較小值，再加總」定義一種成分權重重疊指標。這是本課明示的數學口徑，不是所有平台的統一規則。",
      "重疊指標描述持有內容，不是報酬相關係數，也不能算出最大虧損。即使完全沒有相同證券，仍可能受同一產業或經濟因素影響；持股名單不是完整風險分析。"
    ],
    "example": {
      "title": "假設資料，練習自己核對",
      "steps": [
        "基金 A 有甲 60%、乙 25%、丁 15%；基金 B 有甲 20%、丙 70%、丁 10%。它們都是虛構的全股票基金，持股完整加總 100%。",
        "總資金各放一半，甲的合併權重＝50%×60%＋50%×20%＝40%。",
        "乙、丙、丁分別為 12.5%、35%、12.5%，合計四種證券、總權重 100%。",
        "依本課重疊定義，甲取 20%、丁取 10%，相加為 30%。這不是說整體組合只有 30% 承擔風險。"
      ],
      "conclusion": "先拆開基金持有內容，再看自己的資金如何分配。"
    },
    "pitfalls": [
      "把兩檔基金當成兩份獨立的風險。",
      "把重疊 30% 解讀成相關係數 0.3。"
    ],
    "glossary": [
      {
        "term": "穿透權重",
        "definition": "把基金在組合的比重乘以基金內部持股權重，再合併相同證券。"
      },
      {
        "term": "成分權重重疊",
        "definition": "本課定義為各共同證券在兩基金中較小權重之加總，不等於報酬相關性。"
      }
    ],
    "takeaway": "先拆開基金持有內容，再看自己的資金如何分配。",
    "exercise": "把本例的 A 比重改成 75%，重算四家公司的合併權重，並確認合計為 100%。",
    "quiz": {
      "question": "A 在組合占 70%、其中甲占 50%；B 占 30%、其中甲占 20%。甲的合併權重是多少？",
      "options": [
        "35%，只看 A 的貢獻",
        "70%，直接相加兩基金內部比重",
        "41%，把兩條持有途徑都算入"
      ],
      "answer": 2,
      "explanation": "70%×50%＋30%×20%＝41%。這只是持有比例，不代表甲的未來報酬。"
    },
    "sources": [
      {
        "label": "市場先生｜ETF 購買前的比較項目",
        "url": "https://rich01.com/how-buy-etfs/",
        "kind": "reading"
      },
      {
        "label": "SEC｜指數基金與投資內容",
        "url": "https://www.investor.gov/introduction-investing/investing-basics/investment-products/mutual-funds-and-exchange-traded-4",
        "kind": "official"
      }
    ]
  },
  {
    "id": "fund-documents",
    "moduleId": "research",
    "title": "基金月報、公開說明書，要先看哪一頁？",
    "subtitle": "把產品規則與當期快照分開",
    "minutes": 6,
    "tag": "基金研究",
    "objectives": [
      "記錄文件日期與資料口徑",
      "用原始文件交叉查核費用、策略與持股"
    ],
    "paragraphs": [
      "先確認基金全名、代碼或識別碼、股份類別及文件日期。公開說明書可查目標、策略、風險與費用；股東報告及基金公布的資料則提供特定期間的營運與持有資訊。文件格式與揭露安排依市場和產品而不同。",
      "一張持股表是某個時間點的快照，不保證此刻仍相同。「前十大」也不是完整持股。如果只公布一部分，應標明未知權重，不能把沒有列出的公司都當作零，也不能擅自把已知部分放大到 100%。",
      "閱讀時可做一張查核表：這個數字描述哪個期間、使用哪種幣別與分母、涵蓋全部還是部分、來源是哪份文件？碰到不同日期或定義，先解釋差異，而不是挑最符合期待的數字。"
    ],
    "example": {
      "title": "假設資料，練習自己核對",
      "steps": [
        "某假想月報的前十大權重合計 72%，資料日為 9 月 30 日；今天看到另一張 10 月 31 日的表，合計 68%。",
        "第一張仍有 28% 未列在這張摘要，第二張有 32%。只知道前十大，不能補出其餘持股。",
        "兩張快照不是同一天，差異可能含市場變動或交易；需要完整資料與變動說明才能判斷。"
      ],
      "conclusion": "把文件日期和涵蓋範圍寫在數字旁邊，才能避免誤讀。"
    },
    "pitfalls": [
      "把前十大名單當作完整投資組合。",
      "混用不同股份類別與期間的費用數字。"
    ],
    "glossary": [
      {
        "term": "持股快照",
        "definition": "描述指定日期的持有狀態，可能在之後改變。"
      },
      {
        "term": "未列示權重",
        "definition": "摘要沒有揭露的部分；未知不等於沒有持有。"
      }
    ],
    "takeaway": "把文件日期和涵蓋範圍寫在數字旁邊，才能避免誤讀。",
    "exercise": "找一份基金公開資料，只記下名稱、資料日期、前三項查核問題，不需要買入或輸入帳號。",
    "quiz": {
      "question": "某摘要列出的持股合計 80%，要做全組合重疊分析，應怎麼處理？",
      "options": [
        "標示其餘 20% 未知，取得完整同日資料再判讀",
        "把列出的權重各除以 80%，當成完整持股",
        "把未列的公司一律當成零"
      ],
      "answer": 0,
      "explanation": "已知 80% 不能代表全部持有內容；把它正規化會改變實際權重。"
    },
    "sources": [
      {
        "label": "市場先生｜ETF 購買前的比較項目",
        "url": "https://rich01.com/how-buy-etfs/",
        "kind": "reading"
      },
      {
        "label": "SEC｜閱讀基金股東報告",
        "url": "https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins/updated-investor-bulletin-how-read-mutual-fund-or-etf-shareholder-report",
        "kind": "official"
      }
    ]
  },
  {
    "id": "benchmark-comparison",
    "moduleId": "research",
    "title": "兩張績效圖，怎樣才算公平比較？",
    "subtitle": "期間、幣別、配息與費用口徑",
    "minutes": 6,
    "tag": "基金研究",
    "objectives": [
      "核對總報酬和價格報酬",
      "避免重複扣費及事後更換基準"
    ],
    "paragraphs": [
      "先讓比較雙方回答同一個問題：相同起訖日期、同一幣別、是否計入配息、是否再投入，以及哪些費用和稅已反映。指數、基金淨值與投資人實際成交價格，可能各自使用不同口徑。",
      "基準應能說明要比較的市場或策略，不宜在結果出現後只挑較弱的對手。即使資料口徑一致，超越某基準也不會直接證明承擔相同風險；配置、集中程度與波動仍要另看。",
      "基金公布的淨值報酬通常已反映內部營運費用，但投資人另付的交易費、價差或稅可能未含在內。先查說明，不能把相同費用再扣一次；若查不到口徑，就先保留無法比較的結論。"
    ],
    "example": {
      "title": "假設資料，練習自己核對",
      "steps": [
        "某假想基金期初淨值 100、期末 103，期末另配 2 元現金；不再投入、無額外現金流。",
        "本例淨值已反映內部費用，基金總報酬為（103＋2−100）÷100＝5%。",
        "同期間、同幣別且一致含息口徑的假想指數報酬為 6%，基金相差 −1 個百分點。不能把 3% 的淨值漲幅直接拿來比較 6% 含息指數。"
      ],
      "conclusion": "先對齊口徑再算差額，最後再問風險是否可比。"
    },
    "pitfalls": [
      "把價格報酬和含息報酬當成同一數字。",
      "把已反映在淨值內的管理費再扣一次。"
    ],
    "glossary": [
      {
        "term": "比較口徑",
        "definition": "報酬的期間、幣別、現金流與費用等計算條件。"
      },
      {
        "term": "百分點差",
        "definition": "兩個以百分比表示的數值直接相減得到的差額。"
      }
    ],
    "takeaway": "先對齊口徑再算差額，最後再問風險是否可比。",
    "exercise": "對兩份績效表列出五個口徑；有一項不清楚時，先寫下需要查證的文件。",
    "quiz": {
      "question": "基金同口徑總報酬 4.5%，基準 5.2%，基金減基準是多少？",
      "options": [
        "−0.7%，表示基金最大可能損失",
        "−0.7 個百分點，尚不能單憑差額判定風險或適合度",
        "＋0.7 個百分點，因為基準比較高"
      ],
      "answer": 1,
      "explanation": "4.5%−5.2%＝−0.7 個百分點。差額不是最大虧損估計。"
    },
    "sources": [
      {
        "label": "市場先生｜ETF 購買前的比較項目",
        "url": "https://rich01.com/how-buy-etfs/",
        "kind": "reading"
      },
      {
        "label": "SEC｜費用表與績效口徑",
        "url": "https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins/how-read-0",
        "kind": "official"
      }
    ]
  }
];
